<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ClosedPeriodFields from './ClosedPeriodFields.vue';
import {
  closedPeriodFromStorage,
  closedPeriodToStorage,
  emptyClosedPeriodForm,
  isPastClosedPeriodForm,
} from './closedPeriodHelpers';

const props = defineProps({
  schedule: { type: Object, default: () => null },
  saving: { type: Boolean, default: false },
});
const emit = defineEmits(['updateClosedPeriods']);
const PAGE_SIZE = 5;
const TABS = ['active', 'archived'];

const { t } = useI18n();

const buildClosedPeriods = () =>
  (props.schedule?.closed_periods || []).map(period =>
    closedPeriodFromStorage(period)
  );

const closedPeriods = ref(buildClosedPeriods());
const activeTab = ref('active');
const visibleLimit = ref(PAGE_SIZE);

watch(
  () => props.schedule,
  () => {
    closedPeriods.value = buildClosedPeriods();
    visibleLimit.value = PAGE_SIZE;
  },
  { deep: true }
);

const activeEntries = computed(() =>
  closedPeriods.value
    .map((period, index) => ({ period, index }))
    .filter(({ period }) => !isPastClosedPeriodForm(period))
);

const archivedEntries = computed(() =>
  closedPeriods.value
    .map((period, index) => ({ period, index }))
    .filter(({ period }) => isPastClosedPeriodForm(period))
);

const isArchivedTab = computed(() => activeTab.value === 'archived');

const visibleEntries = computed(() =>
  isArchivedTab.value ? archivedEntries.value : activeEntries.value
);

const shownEntries = computed(() =>
  visibleEntries.value.slice(0, visibleLimit.value)
);

const hasMore = computed(
  () => visibleEntries.value.length > visibleLimit.value
);

const tabsForTabBar = computed(() => [
  {
    key: 'active',
    label: t('SETTINGS.SPACES.CLOSED_PERIODS.TAB_ACTIVE'),
    count: activeEntries.value.length || undefined,
  },
  {
    key: 'archived',
    label: t('SETTINGS.SPACES.CLOSED_PERIODS.TAB_ARCHIVED'),
    count: archivedEntries.value.length || undefined,
  },
]);

const activeTabIndex = computed(() =>
  Math.max(TABS.indexOf(activeTab.value), 0)
);

const onTabChange = tab => {
  activeTab.value = tab.key;
  visibleLimit.value = PAGE_SIZE;
};

const addClosedPeriod = () => {
  closedPeriods.value.push(emptyClosedPeriodForm());
  activeTab.value = 'active';
  visibleLimit.value = Math.max(visibleLimit.value, activeEntries.value.length);
};

const removeClosedPeriod = index => {
  closedPeriods.value.splice(index, 1);
};

const loadMore = () => {
  visibleLimit.value += PAGE_SIZE;
};

const saveClosedPeriods = () => {
  emit(
    'updateClosedPeriods',
    closedPeriods.value
      .map(period => closedPeriodToStorage(period))
      .filter(period => period.start && period.end)
  );
};
</script>

<template>
  <div class="space-y-4 pt-2">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <TabBar
        :tabs="tabsForTabBar"
        :initial-active-tab="activeTabIndex"
        @tab-changed="onTabChange"
      />
      <Button
        v-if="!isArchivedTab"
        size="sm"
        variant="faded"
        color="slate"
        icon="i-lucide-plus"
        :label="t('SETTINGS.SPACES.SCHEDULE.ADD_CLOSED_PERIOD')"
        @click="addClosedPeriod"
      />
    </div>

    <p class="text-xs text-n-slate-10">
      {{
        isArchivedTab
          ? t('SETTINGS.SPACES.CLOSED_PERIODS.ARCHIVED_DESCRIPTION')
          : t('SETTINGS.SPACES.SCHEDULE.CLOSED_PERIODS_DESCRIPTION')
      }}
    </p>

    <p
      v-if="visibleEntries.length === 0"
      class="py-6 text-center text-sm text-n-slate-10"
    >
      {{
        isArchivedTab
          ? t('SETTINGS.SPACES.CLOSED_PERIODS.NO_ARCHIVED')
          : t('SETTINGS.SPACES.CLOSED_PERIODS.NO_CURRENT')
      }}
    </p>

    <div v-else class="divide-y divide-n-weak">
      <div
        v-for="{ period, index } in shownEntries"
        :key="index"
        class="flex items-start gap-3 py-4 first:pt-1 last:pb-1"
        :class="{ 'opacity-80': isArchivedTab }"
      >
        <div class="min-w-0 flex-1">
          <ClosedPeriodFields
            v-model="closedPeriods[index]"
            :readonly="isArchivedTab"
          />
        </div>
        <button
          type="button"
          class="mt-7 flex size-8 shrink-0 items-center justify-center rounded-md text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-ruby-9"
          :aria-label="t('SETTINGS.SPACES.SCHEDULE.REMOVE')"
          @click="removeClosedPeriod(index)"
        >
          <span class="i-lucide-trash-2 size-4" aria-hidden="true" />
        </button>
      </div>
    </div>

    <div v-if="hasMore" class="flex justify-center">
      <Button
        size="sm"
        variant="link"
        color="slate"
        :label="t('SETTINGS.SPACES.CLOSED_PERIODS.LOAD_MORE')"
        @click="loadMore"
      />
    </div>

    <div class="flex justify-end border-t border-n-weak pt-4">
      <Button
        :label="t('SETTINGS.SPACES.CLOSED_PERIODS.SAVE')"
        :loading="saving"
        @click="saveClosedPeriods"
      />
    </div>
  </div>
</template>
